"use client";

import { ArrowClockwise, Tray, WarningCircle } from "@phosphor-icons/react";
import { useId, type ComponentProps, type ReactNode } from "react";
import { Button } from "../ui/button";
import { cn } from "../lib/utils";
import { useKitseed } from "../lib/provider";
import "./patterns.css";

export function ListSkeleton({
  rows = 5,
  label = "正在加载",
  className,
}: {
  rows?: number;
  label?: string;
  className?: string;
}) {
  return (
    <div
      role="status"
      aria-label={label}
      aria-busy="true"
      className={cn(
        "divide-y divide-line overflow-hidden rounded-panel border border-line bg-panel",
        className,
      )}
    >
      {Array.from({ length: Math.max(1, Math.min(20, rows)) }, (_, index) => (
        <div key={index} aria-hidden="true" className="flex items-center gap-3 px-4 py-4">
          <span className="size-8 shrink-0 rounded-control bg-hover motion-safe:animate-pulse" />
          <div className="min-w-0 flex-1 space-y-2">
            <span
              className="block h-3 rounded bg-hover motion-safe:animate-pulse"
              style={{ width: `${52 + ((index * 13) % 34)}%` }}
            />
            <span
              className="block h-2.5 rounded bg-hover motion-safe:animate-pulse"
              style={{ width: `${28 + ((index * 17) % 26)}%` }}
            />
          </div>
          <span className="h-2.5 w-12 rounded bg-hover motion-safe:animate-pulse" />
        </div>
      ))}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  variant = "default",
  className,
}: {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  variant?: "default" | "onboarding" | "compact";
  className?: string;
}) {
  const id = useId();
  return (
    <section
      aria-labelledby={id}
      data-variant={variant}
      className={cn("ks-empty-state grid place-items-center px-5 py-10 text-center", className)}
    >
      <div className="grid max-w-sm justify-items-center">
        <span aria-hidden="true" className="ks-empty-art">
          <span className="grid size-11 place-items-center rounded-panel border border-line-strong bg-panel text-fg-muted shadow-xs">
            {icon ?? <Tray size={20} />}
          </span>
        </span>
        <h2 id={id} className="mt-4 text-sm font-semibold text-fg text-balance">
          {title}
        </h2>
        {description ? (
          <div className="mt-2 text-[13px] leading-6 text-fg-muted text-pretty">{description}</div>
        ) : null}
        {action ? <div className="mt-5 flex flex-wrap justify-center gap-2">{action}</div> : null}
      </div>
    </section>
  );
}

export type AlertProps = Omit<ComponentProps<"div">, "title"> & {
  tone?: "danger" | "success" | "info" | "warn";
  title?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
};
export function Alert({
  tone = "info",
  title,
  icon,
  action,
  children,
  className,
  ...props
}: AlertProps) {
  const styles = {
    danger: "border-danger-line bg-danger-subtle text-danger-text",
    success: "border-success-line bg-success-subtle text-success-text",
    info: "border-brand-line bg-brand-subtle text-brand-text",
    warn: "border-warn-line bg-warn-subtle text-warn-text",
  }[tone];
  return (
    <div
      role={tone === "danger" ? "alert" : "status"}
      {...props}
      className={cn(
        "flex min-w-0 items-start gap-2.5 rounded-control border px-3 py-2.5 text-[13px] leading-6",
        styles,
        className,
      )}
    >
      {icon ? (
        <span aria-hidden="true" className="mt-1 shrink-0">
          {icon}
        </span>
      ) : null}
      <div className="min-w-0 flex-1 [overflow-wrap:anywhere]">
        {title ? <div className="font-medium">{title}</div> : null}
        {children}
        {action ? <div className="mt-2">{action}</div> : null}
      </div>
    </div>
  );
}

export function ErrorState({
  title = "暂时无法加载",
  description,
  onRetry,
  retryLabel,
  details,
  className,
}: {
  title?: ReactNode;
  description?: ReactNode;
  onRetry?: () => void;
  retryLabel?: string;
  details?: ReactNode;
  className?: string;
}) {
  const { messages } = useKitseed();
  return (
    <div
      role="alert"
      className={cn(
        "grid min-h-56 place-items-center rounded-panel border border-danger-line bg-danger-subtle px-6 py-9 text-center",
        className,
      )}
    >
      <div className="grid max-w-sm justify-items-center">
        <span
          aria-hidden="true"
          className="grid size-10 place-items-center rounded-panel border border-danger-line bg-panel text-danger"
        >
          <WarningCircle size={20} />
        </span>
        <h2 className="mt-3.5 text-sm font-semibold text-fg">{title}</h2>
        {description ? (
          <div className="mt-2 text-[13px] leading-6 text-fg-muted">{description}</div>
        ) : null}
        {details ? (
          <div className="mt-3 w-full text-left text-xs text-fg-muted">{details}</div>
        ) : null}
        {onRetry ? (
          <Button variant="secondary" className="mt-4" onClick={onRetry}>
            <ArrowClockwise size={14} aria-hidden="true" />
            {retryLabel ?? messages.retry}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export function RefreshNotice({
  children,
  onRetry,
  retryLabel = "重新刷新",
  className,
}: {
  children: ReactNode;
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "flex flex-wrap items-center justify-between gap-2 rounded-control border border-line bg-hover/40 px-3 py-2 text-xs leading-5 text-fg-muted",
        className,
      )}
    >
      <span>{children}</span>
      {onRetry ? (
        <Button variant="ghost" size="sm" onClick={onRetry}>
          <ArrowClockwise size={13} aria-hidden="true" />
          {retryLabel}
        </Button>
      ) : null}
    </div>
  );
}
